import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useFetchLocation } from '@/lib/hooks/server'
import * as z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Label } from '@/components/ui/label'
import CPFInput, { validaCPF } from '../../../components/form/input/cpf-mask-input'
import { useCheckPublicWitness } from '../services'

export const photoCaptureSchema = z.object({
  name: z.string().min(3, 'Informe o nome completo'),
  cpf: z
    .string()
    .min(11, 'CPF obrigatório')
    .refine(v => validaCPF(v), 'CPF inválido')
})

export type PhotoCaptureFormData = z.infer<typeof photoCaptureSchema>

export interface CapturedData {
  local: string
  maquina: string
  currentDateTime: string
  name: string
  cpf: string
}

interface PhotoCaptureProps {
  onComplete: (data: CapturedData) => void
}

export default function PhotoCaptureWitness({ onComplete }: PhotoCaptureProps) {
  const [location, setLocation] = useState<string>('Carregando...')
  const [deviceInfo, setDeviceInfo] = useState<string>('')
  const [currentDateTime, setCurrentDateTime] = useState<string>('')
  const [witnessCheckedCpf, setWitnessCheckedCpf] = useState('')

  const form = useForm<PhotoCaptureFormData>({
    resolver: zodResolver(photoCaptureSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      cpf: ''
    }
  })

  const { mutateAsync: checkPublicWitness, isPending: isCheckingWitness } = useCheckPublicWitness()
  const { data: locationData, error: locationError } = useFetchLocation()

  useEffect(() => {
    const now = new Date()
    setCurrentDateTime(now.toLocaleString())
    setDeviceInfo(navigator.userAgent || 'Informações não disponíveis')
  }, [])

  useEffect(() => {
    if (locationData && typeof locationData === 'object' && 'city' in locationData && 'region' in locationData) {
      setLocation(
        `${locationData.city}, ${locationData.region}, ${locationData.country}, ${locationData.zip} (lat: ${locationData.lat}, lon: ${locationData.lon})`
      )
    } else if (locationError) {
      setLocation('Erro ao obter localização.')
    } else {
      setLocation('Localização não disponível.')
    }
  }, [locationData, locationError])

  const onSubmit = (data: PhotoCaptureFormData) => {
    onComplete({
      local: location,
      maquina: deviceInfo,
      currentDateTime,
      name: data.name,
      cpf: data.cpf
    })
  }

  const handleCheckWitness = async () => {
    const isCpfValid = await form.trigger('cpf')
    if (!isCpfValid) return

    const cpf = form.getValues('cpf')
    const result = await checkPublicWitness(cpf)

    setWitnessCheckedCpf(cpf)

    if (result.exists && result.witness?.nome && !form.getValues('name')) {
      form.setValue('name', result.witness.nome, { shouldValidate: true, shouldDirty: true })
    }
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-64 shadow-xl">
      <div className="bg-background p-6 rounded-lg shadow-md w-full max-w-2xl">
        <h2 className="text-2xl font-bold mb-6 text-center">Dados da Testemunha</h2>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="name">Nome completo</FormLabel>
                    <FormControl>
                      <Input id="name" placeholder="Digite o nome completo" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cpf"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="cpf">CPF</FormLabel>
                    <FormControl>
                      <CPFInput
                        id="cpf"
                        placeholder="Digite o CPF"
                        value={field.value || ''}
                        onCPFChange={(value: string) =>
                          form.setValue('cpf', value, { shouldValidate: true, shouldDirty: true })
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="md:col-span-2 flex items-center gap-3">
                <Button type="button" variant="outline" onClick={handleCheckWitness} disabled={isCheckingWitness}>
                  {isCheckingWitness ? 'Verificando CPF...' : 'Verificar CPF'}
                </Button>
                {witnessCheckedCpf && (
                  <p className="text-sm text-muted-foreground">
                    Se a testemunha já existir, o nome será preenchido automaticamente.
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 rounded-md border p-4 text-sm">
              <div>
                <Label>Data e hora</Label>
                <p className="text-muted-foreground">{currentDateTime}</p>
              </div>
              <div>
                <Label>Localização</Label>
                <p className="text-muted-foreground break-words">{location}</p>
              </div>
              <div>
                <Label>Informações do dispositivo</Label>
                <p className="text-muted-foreground break-words">{deviceInfo}</p>
              </div>
            </div>

            <Button type="submit" className="w-full">
              Continuar para assinatura
            </Button>
          </form>
        </Form>
      </div>
    </div>
  )
}
